import React, { useCallback, useState } from 'react';
import { reportsAPI } from '../../services/api';
import { formatClinicDate } from '../../utils/clinicTime';
import useReport from './shared/useReport';
import KpiStrip from './shared/KpiStrip';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import DateRangeFilter, { getPresetRange } from './shared/DateRangeFilter';
import { fmtNum } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// Work started at either end of a visit and left (owner, 2026-10-06). Two lists, because it is the
// same question asked of the two ends and whoever reviews one wants the other: a token issued that
// nobody attended to, and a prescription the doctor opened and never finished writing.
//
// WHAT "NOT CALLED" HAD TO MEAN. Taken literally it is `doctor_called_at IS NULL`, and that would
// have been useless: the column is stamped only when somebody presses Call on the queue, while a
// doctor who opens the prescription straight from their list sends the visit to IN_CONSULTATION
// without it. On production every completed consultation has no call stamp. So the list is of
// visits still PENDING_VITALS or READY_FOR_DOCTOR: tokens nobody got to, whichever route is used.
//
// A cancelled token is left out on purpose: it was dealt with, not abandoned.

const STATUS_LABEL = {
  PENDING_VITALS: 'Waiting for vitals',
  READY_FOR_DOCTOR: 'Waiting for the doctor',
  INITIATED: 'Opened, nothing written',
  DRAFT: 'Part written',
};

const label = (s) => STATUS_LABEL[s] || s || '';

/** Days are the point of both lists, so the longer something has sat the louder it reads. */
const days = (n) => {
  const v = Number(n) || 0;
  const tone = v >= 7 ? 'text-rose-600 font-semibold' : v >= 2 ? 'text-amber-600 font-medium' : 'text-gray-600';
  return <span className={tone}>{v}</span>;
};

const TOKEN_COLUMNS = [
  {
    key: 'visitDate',
    label: 'Issued',
    render: (r) => formatClinicDate(r.visitDate),
    csv: (r) => r.visitDate,
  },
  { key: 'token', label: 'Token' },
  {
    key: 'patient',
    label: 'Patient',
    render: (r) => (
      <span>
        {r.patient}
        {r.emergency && <span className="ml-2 text-xs font-semibold text-rose-600">Emergency</span>}
      </span>
    ),
    csv: (r) => (r.emergency ? `${r.patient} (emergency)` : r.patient),
  },
  { key: 'mrn', label: 'MRN' },
  { key: 'phone', label: 'Phone' },
  { key: 'doctor', label: 'Doctor' },
  {
    key: 'status',
    label: 'Stopped at',
    render: (r) => label(r.status),
    csv: (r) => label(r.status),
  },
  { key: 'daysWaiting', label: 'Days', align: 'right', render: (r) => days(r.daysWaiting), csv: (r) => r.daysWaiting },
];

const RX_COLUMNS = [
  {
    key: 'startedOn',
    label: 'Started',
    render: (r) => formatClinicDate(r.startedOn),
    csv: (r) => r.startedOn,
  },
  { key: 'prescriptionNumber', label: 'Prescription #' },
  { key: 'patient', label: 'Patient' },
  { key: 'mrn', label: 'MRN' },
  { key: 'doctor', label: 'Doctor' },
  {
    key: 'status',
    label: 'Stopped at',
    render: (r) => label(r.status),
    csv: (r) => label(r.status),
  },
  {
    key: 'diagnosis',
    label: 'Diagnosis',
    render: (r) => r.diagnosis || <span className="text-gray-400">None yet</span>,
    csv: (r) => r.diagnosis || '',
  },
  { key: 'medicines', label: 'Medicines', align: 'right' },
  { key: 'daysSince', label: 'Days', align: 'right', render: (r) => days(r.daysSince), csv: (r) => r.daysSince },
];

const UnfinishedVisitsReport = () => {
  const drill = useDrill();
  const [range, setRange] = useState(() => getPresetRange('thisMonth'));
  const [preset, setPreset] = useState('thisMonth');

  const fetcher = useCallback(
    () => reportsAPI.getUnfinishedVisits({ from: range.from, to: range.to }),
    [range],
  );
  const { data, loading } = useReport(fetcher);

  const k = data?.kpis || {};
  const kpis = [
    {
      label: 'Tokens Never Seen',
      value: fmtNum(k.tokensNeverSeen ?? 0),
      tone: Number(k.tokensNeverSeen) > 0 ? 'alert' : undefined,
    },
    { label: 'Waiting For Vitals', value: fmtNum(k.waitingForVitals ?? 0) },
    {
      label: 'Prescriptions Unfinished',
      value: fmtNum(k.prescriptionsUnfinished ?? 0),
      tone: Number(k.prescriptionsUnfinished) > 0 ? 'alert' : undefined,
    },
    { label: 'Nothing Written Into', value: fmtNum(k.neverWrittenInto ?? 0) },
    { label: 'Oldest', value: `${k.oldestDaysWaiting ?? 0} days` },
  ];

  const tokens = data?.tokens || [];
  const prescriptions = data?.prescriptions || [];
  const cap = data?.limit ?? 200;

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <DateRangeFilter
            value={range}
            preset={preset}
            onChange={(r, p) => { setRange(r); setPreset(p); }}
          />
        </div>
        <div className="pt-4"><PrintButton /></div>
      </div>

      <p className="text-sm text-gray-500">
        Work that was started and left. A token counts here when it never got past waiting, whatever
        the reason; a prescription counts when the doctor opened it and never finished writing it.
        Cancelled tokens are left out, and so is a prescription the doctor finished, which the
        dispensary takes from there.
      </p>

      <KpiStrip items={kpis} loading={loading} />

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'patient', mrn: r.mrn })}
        title="Tokens issued that never reached the doctor"
        columns={TOKEN_COLUMNS}
        rows={tokens}
        csvName={`tokens-never-seen-${range.from}-to-${range.to}`}
        emptyText="Every token issued in this period reached the doctor."
      />
      {data?.tokensTruncated && (
        <p className="text-xs text-amber-700">
          Showing the {fmtNum(cap)} most recent of {fmtNum(k.tokensNeverSeen)}. Narrow the dates to see the rest.
        </p>
      )}

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'patient', mrn: r.mrn })}
        title="Prescriptions started and never finished"
        columns={RX_COLUMNS}
        rows={prescriptions}
        csvName={`prescriptions-unfinished-${range.from}-to-${range.to}`}
        emptyText="Every prescription started in this period was finished."
      />
      {data?.prescriptionsTruncated && (
        <p className="text-xs text-amber-700">
          Showing the {fmtNum(cap)} most recent of {fmtNum(k.prescriptionsUnfinished)}. Narrow the dates to see the rest.
        </p>
      )}

      <ReportPrintLayout
        title="Unfinished Visits"
        subtitle={`Between ${range.from} and ${range.to}`}
        kpis={kpis.map(({ label: l, value }) => ({ label: l, value }))}
        tables={[
          { title: 'Tokens issued that never reached the doctor', columns: TOKEN_COLUMNS, rows: tokens },
          { title: 'Prescriptions started and never finished', columns: RX_COLUMNS, rows: prescriptions },
        ]}
      />

      {drill.node}
    </div>
  );
};

export default UnfinishedVisitsReport;
